import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import useConsultantBookingsForm from '../hooks/useConsultantBookingForm';
import CTAButton from '../components/CTAButton';
import InitialBookingDisclaimer from '../components/InitialBookingDisclaimer';
import { ReactComponent as Checkmark } from '../static/img/checkmark.svg';
import ConsultantBookingError from '../components/ConsultantBookingError';
import ConsultantPayment from '../components/ConsultantPayment';
import CommunicationCard from '../components/CommunicationCard';

import './ConsultantSummary.sass';
import consultantBookingErrorActions from '../actions/consultantBookingErrorActions';
import ConsultantCardholderDetails from '../components/ConsultantCardholderDetails';
import Accordion from '../components/CardholderAccordion';

export default () => {
  const { state } = useLocation();
  const history = useHistory();
  const { logConsultantBookingError: logError } = consultantBookingErrorActions();
  const [selectedCardholderDetailsOption, setSelectedCardholderDetailsOption] = useState('');
  const [paymentToken, setPaymentToken] = useState(false);
  const [showCtaError, setShowCtaError] = useState(false);
  const [checkoutRendered, setCheckoutRendered] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState();
  const [error, setError] = useState('');
  const [cardAttempts, setCardAttempts] = useState(0);
  const [checkoutId, setCheckoutId] = useState();
  const [checkoutBilling, setCheckoutBilling] = useState();

  const { cardholderForm, toggleTerms, summaryAction, canSubmitSummaryAction } =
    useConsultantBookingsForm({
      ...state,
      initialValues: state.formValues || {},
      paymentToken,
      checkoutId,
      checkoutBilling,
    });

  const handleCardholderDetailsChange = value => {
    setSelectedCardholderDetailsOption(value);
  };

  const errorState = useSelector(s => s.consultantBookingError);
  const showConsultantError = Boolean(
    errorState && (errorState.error?.errorCode || errorState.text || errorState.error),
  );

  useEffect(() => {
    window.parent.postMessage(
      {
        event_id: 'dataLayer',
        data: {
          event: 'consultantBookingSummaryLoaded',
          journey_type: state.mandatoryPayment
            ? 'mandatory'
            : state.optionalPayment
              ? 'optional'
              : 'not in trial',
          payment_method: state.formValues.payment,
          pmi_name: state.formValues.insurer,
        },
      },
      state.parentURL,
    );
  }, [
    state.mandatoryPayment,
    state.optionalPayment,
    state.formValues.payment,
    state.formValues.insurer,
    state.parentURL,
  ]);

  useEffect(() => {
    if (checkoutRendered) {
      window.parent.postMessage(
        {
          event_id: 'dataLayer',
          data: {
            event: 'consultantPaymentWidgetLoaded',
            journey_type: state.mandatoryPayment
              ? 'mandatory'
              : state.optionalPayment
                ? 'optional'
                : 'not in trial',
            cardholder_details:
              selectedCardholderDetailsOption === 'same'
                ? 'same as patient details'
                : 'different to patient details',
          },
        },
        state.parentURL,
      );
    }
  }, [
    checkoutRendered,
    state.optionalPayment,
    state.mandatoryPayment,
    selectedCardholderDetailsOption,
    state.parentURL,
  ]);

  useEffect(() => {
    if (paymentStatus === 'successful' || paymentStatus === 'unsuccessful') {
      window.parent.postMessage(
        {
          event_id: 'dataLayer',
          data: {
            event: 'consultantPaymentStatus',
            journey_type: state.mandatoryPayment
              ? 'mandatory'
              : state.optionalPayment
                ? 'optional'
                : 'not in trial',
            payment_status: paymentStatus,
          },
        },
        state.parentURL,
      );
    }
  }, [paymentStatus, state.optionalPayment, state.mandatoryPayment, state.parentURL]);

  useEffect(() => {
    if (paymentStatus === 'successful' || error || cardAttempts >= 3) {
      const paymentStatusWrapper = document.querySelector('.payment-status-wrapper');
      if (paymentStatusWrapper) {
        paymentStatusWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [paymentStatus, error, cardAttempts]);

  const cardholderDetails = () => {
    return (
      <ConsultantCardholderDetails
        cardholderForm={cardholderForm}
        patientForm={state.formValues}
        hospitalId={state.hospital.hospitalId}
        onChange={handleCardholderDetailsChange}
        setPaymentToken={setPaymentToken}
        setCheckoutRendered={setCheckoutRendered}
        setPaymentStatus={setPaymentStatus}
        paymentStatus={paymentStatus}
        error={error}
        setError={setError}
        cardAttempts={cardAttempts}
        setCardAttempts={setCardAttempts}
        setCheckoutId={setCheckoutId}
        setCheckoutBilling={setCheckoutBilling}
      />
    );
  };

  function capitaliseFirstLetter(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return (
    <>
      <InitialBookingDisclaimer />
      <div className="m--consultant-summary">
        <div className="pattern" />
        <div className="header__wrapper mb-40">
          <div className="header">
            <h2 className="h2">Book consultant</h2>
          </div>
        </div>
        <ConsultantBookingError />
        {!showConsultantError && (
          <>
            <div className="consultant-summary">
              <h3 className="consultant-summary__heading">Booking summary</h3>
              <p className="consultant-summary-sub-text">
                Your appointment with {state.consultant.name} on{' '}
                {moment(state.slotDate).format('dddd, Do MMMM YYYY')} at {state.slotTime}.
              </p>
              <div className="consultant-summary-data">
                <div className="consultant-summary-h4-edit">
                  <h3>Patient details</h3>
                  {!paymentToken && (
                    <CTAButton
                      className="back-btn"
                      action={{
                        call: async () => {
                          history.replace(
                            `/consultants/${state.consultant.gmcCode}/details/`,
                            state,
                          );
                        },
                        name: 'Edit',
                        type: 'EDIT',
                      }}
                    />
                  )}
                </div>
                <p className="consultant-summary-sub-text">
                  Please review your details and complete payment authorisation.
                </p>
                <div>
                  <p className="consultant-summary-title">Title</p>
                  <p className="consultant-summary-value">{state.formValues.title}</p>
                </div>
                <div>
                  <p className="consultant-summary-title">First name</p>
                  <p className="consultant-summary-value">{state.formValues.first_name}</p>
                </div>
                <div>
                  <p className="consultant-summary-title">Last name</p>
                  <p className="consultant-summary-value">{state.formValues.last_name}</p>
                </div>
                <div>
                  <p className="consultant-summary-title">Email</p>
                  <p className="consultant-summary-value">{state.formValues.email}</p>
                </div>
                <div>
                  <p className="consultant-summary-title">Your address</p>
                  <p className="consultant-summary-value">{`${state.formValues.addresslookup.line_one}, ${state.formValues.addresslookup.line_two ? state.formValues.addresslookup.line_two + ',' : ''} ${state.formValues.addresslookup.city}, ${state.formValues.addresslookup.postal_code}`}</p>
                </div>
                <div>
                  <p className="consultant-summary-title">Gender</p>
                  <p className="consultant-summary-value">
                    {state.formValues.gender === 'other'
                      ? 'Other / Prefer not to say'
                      : capitaliseFirstLetter(state.formValues.gender)}
                  </p>
                </div>
                <div>
                  <p className="consultant-summary-title">Date of birth</p>
                  <p className="consultant-summary-value">
                    {moment({
                      ...state.formValues.date_of_birth,
                      month: state.formValues.date_of_birth.month - 1, // Months are zero-indexed
                    }).format('DD/MM/YYYY')}
                  </p>
                </div>
                <div>
                  <p className="consultant-summary-title">Phone number</p>
                  <p className="consultant-summary-value">{state.formValues.phone_number}</p>
                </div>
                <div>
                  <p className="consultant-summary-title">Do you have a GP referral?</p>
                  <p className="consultant-summary-value">
                    {capitaliseFirstLetter(state.formValues.has_gp_referral)}
                  </p>
                </div>
                <div>
                  <p className="consultant-summary-title">Do you have an NHS GP?</p>
                  <p className="consultant-summary-value">
                    {capitaliseFirstLetter(state.formValues.send_discharge_details_to_gp)}
                  </p>
                </div>
                <div>
                  <p className="consultant-summary-title">How will you pay for the consultation?</p>
                  <p className="consultant-summary-value">
                    {state.formValues.payment === 'cash'
                      ? 'Pay for myself'
                      : 'I have medical insurance'}
                  </p>
                </div>
              </div>
            </div>
            <ConsultantPayment state={state} />
            {state.mandatoryPayment ? (
              cardholderDetails()
            ) : (
              <Accordion>{cardholderDetails()}</Accordion>
            )}
            {(paymentStatus === 'successful' || error || cardAttempts >= 3) && (
              <div className="payment-status-wrapper" style={{ marginTop: '20px' }}>
                {!error && paymentStatus === 'successful' && (
                  <CommunicationCard
                    type="success"
                    title="Authorisation successful"
                    copy="Your payment card details have been stored securely. Please continue to the next step to complete your booking."
                    style={{ margin: 0 }}
                  />
                )}
                {error && cardAttempts < 3 && (
                  <CommunicationCard
                    type="warning"
                    icon="true"
                    title="Sorry, we couldn't complete your booking"
                    copy={`If you still want to proceed with your booking, give us a call on <strong><a href='tel:03001231995'>0300 123 1995</a></strong> or <a href='https://www.nuffieldhealth.com/consultants/${state.consultantName}#book' target='_parent'><strong>restart your booking online here</strong></a>.`}
                  />
                )}
                {cardAttempts >= 3 && (
                  <CommunicationCard
                    type="warning"
                    icon="true"
                    title="Sorry, we weren't able to authorise your card."
                    copy={`If you still want to proceed with your booking, give us a call on <strong><a href='tel:03001231995'>0300 123 1995</a></strong> or <a href='https://www.nuffieldhealth.com/consultants/${state.consultantName}#book' target='_parent'><strong>restart your booking online here</strong></a>.`}
                    style={{ margin: 0 }}
                  />
                )}
              </div>
            )}
            <div className="custom-checkbox__wrapper">
              <input
                className="custom-checkbox__value"
                id="toc"
                type="checkbox"
                onChange={toggleTerms}
              />
              <label className="custom-checkbox__label" htmlFor="toc">
                <span className="custom-checkbox__faux">
                  <Checkmark />
                </span>
                <span>I agree to the </span>
                <a
                  target="_blank"
                  rel="noreferrer noopener"
                  href="https://www.nuffieldhealth.com/terms/nuffield-health-website-terms-and-conditions"
                >
                  terms and conditions
                </a>
                <span> and </span>
                <a
                  target="_blank"
                  rel="noreferrer noopener"
                  href="https://www.nuffieldhealth.com/privacy"
                >
                  privacy policy
                </a>
              </label>
            </div>
            <div className="ctas__wrapper mt-40">
              {!paymentToken && (
                <CTAButton
                  className="back-btn"
                  action={{
                    call: async () => {
                      history.replace(`/consultants/${state.consultant.gmcCode}/details/`, state);
                    },
                    name: 'Back',
                    type: 'BACK',
                  }}
                />
              )}
              <div
                className="cta_wrapper"
                onClick={() => {
                  if (!canSubmitSummaryAction) {
                    setShowCtaError(true);
                  }
                }}
              >
                <CTAButton
                  action={summaryAction}
                  onAction={result => {
                    if (result.statusCode === 'true') {
                      history.replace(`/consultants/${state.consultant.gmcCode}/booked/`, {
                        ...state,
                        cardholderDetails: selectedCardholderDetailsOption,
                        paymentToken,
                        paymentStatus,
                      });
                      window.parent.postMessage(
                        {
                          event_id: 'dataLayer',
                          data: {
                            event: 'consultantBookButtonClicked',
                            cardholderDetails: selectedCardholderDetailsOption,
                          },
                        },
                        state.parentURL,
                      );
                    } else if (result.errorCode === '1033') {
                      // Transaction ID mismatch (this may no longer be needed due to change in reqs)
                      setError(
                        'We were unable to authorise your card. Please re-enter your card details or try a different card.',
                      );
                    } else if (result.errorCode === '1035') {
                      // CVV mismatch
                      setError(
                        'We were unable to authorise your card. Please re-enter your card details or try a different card.',
                      );
                      setPaymentToken(false);
                      setPaymentStatus(null);
                    } else {
                      logError({
                        error: 'CUSTOM',
                        text: 'Sorry but we are unable to process your booking online. Please call 0345 602 9262 to complete your booking.',
                      });
                    }
                  }}
                />
                {showCtaError && state.mandatoryPayment && !paymentToken && (
                  <p className="cta-error-message">
                    Please confirm cardholder details before proceeding.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
